import { useCallback, useMemo } from 'react';
import { ProductSorting } from '../../../models/Product';
import { useParams } from 'react-router';
import { LibxChangeProductPageBuilder } from './shared/LibxChangeProuctPageBuilder';
import { useAllTitlesProducts } from './api/useAllTitlesProducts';
import { QueryKeyBuilder } from '../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../api/listContent/fetchAggregations';
import { fetchAllTitlesAggregations } from './api/fetchAllTitlesAggregations';
import { FetchAggregationsQuickSearch } from '../../api/listContent/fetchAggregationsQuickSearch';
import { fetchAllTitlesAggregationsQuickSearch } from './api/fetchAllTitlesAggregationsQuickSearch';
import { defaultVisibleFilterGroups } from '../../shared/filter/defaultVisibleFilterGroups';
import { useAgeCategorySearchParam } from '../../shared/utils/useAgeCategorySearchParam';
import { DISPLAY_TYPE_MINIMAL } from '../../shared/common/displayType/types';
import { useDisplayTypeSearchParam } from '../../shared/utils/useDisplayTypeSearchParam';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import { usePaginationAndSortParams } from '../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../shared/filter/useProductFilters';

const LIBX_NEWLY_ADDED_DOCUMENT_TITLE = 'LIBXchange | Newly Added';

export const AllTitlesPage = () => {
    const { shopLanguage } = useParams<'shopLanguage'>();

    const authentication = useAuthentication();
    const { page, pageSize, sortBy } = usePaginationAndSortParams({
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD,
        defaultPageSize: 55
    });

    const { productFilter } = useProductFilters();

    const { value: ageCategory } = useAgeCategorySearchParam();
    const { display } = useDisplayTypeSearchParam(DISPLAY_TYPE_MINIMAL);

    const { products, isLoading: isProductsLoading } = useAllTitlesProducts({
        pagination: {
            page,
            pageSize,
            sortBy
        },
        filterMap: productFilter,
        ageCategory,
        shopLanguage
    });

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => defaultVisibleFilterGroups(authentication, ageCategory),
        [ageCategory, authentication]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'byGenrePage',
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups,
            shopLanguage
        ],
        [ageCategory, shopLanguage]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options => fetchAllTitlesAggregations({ ...options, ageCategory, shopLanguage }),
        [ageCategory, shopLanguage]
    );
    const fetchAggregantionsQuickSearch = useCallback<FetchAggregationsQuickSearch>(
        options => fetchAllTitlesAggregationsQuickSearch({ ...options, ageCategory, shopLanguage }),
        [ageCategory, shopLanguage]
    );
    //endregion

    return (
        <LibxChangeProductPageBuilder
            loading={isProductsLoading}
            products={products}
            documentTitle={LIBX_NEWLY_ADDED_DOCUMENT_TITLE}
            displayType={display}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            isNewlyAdded={false}
            isAllTitles={true}
            visibleFilterGroups={visibleFilterGroups}
            buildQueryKey={aggregationsQueryKeyBuilder}
            fetchAggregations={fetchAggregations}
            fetchAggregationsQuickSearch={fetchAggregantionsQuickSearch}
            shopLanguage={shopLanguage}
        />
    );
};
